import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusChip from "../components/StatusChip";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";
const d = (s) => (s ? s.slice(0, 10) : "—");

export default function Workshop() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [returning, setReturning] = useState(null);
  const [f, setF] = useState({ vehicle_id: "", ro_number: "", reason: "Periodic Service", workshop_branch: "", expected_return_date: "", odo_out: "" });
  const [r, setR] = useState({ odo_in: "", work_summary: "", cost_amount: "", cost_category: "Periodic Service" });
  const canEdit = ["tdc", "sm", "admin"].includes(user.role);

  const load = () => {
    api.get("/workshop").then((res) => setEntries(res.data));
    api.get("/vehicles").then((res) => setVehicles(res.data));
  };
  useEffect(load, []);

  const handover = async () => {
    try {
      await api.post("/workshop", { ...f, odo_out: parseInt(f.odo_out) });
      toast.success("Handover recorded — vehicle moved to Workshop"); setShowForm(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doReturn = async () => {
    if (!r.cost_amount) { toast.error("Cost amount in ₹ is mandatory"); return; }
    try {
      await api.post(`/workshop/${returning}/return`, { ...r, odo_in: parseInt(r.odo_in), cost_amount: parseFloat(r.cost_amount) });
      toast.success("Return accepted — vehicle back to Available, cost posted to Accounts"); setReturning(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-4 py-5 lg:px-8 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Workshop</h1>
        {canEdit && <button data-testid="workshop-handover-btn" onClick={() => setShowForm(!showForm)}
          className="rounded-full bg-[#BB162B] px-4 py-2 text-sm font-bold text-white">+ Handover</button>}
      </div>

      {showForm && (
        <div className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
          <select data-testid="ws-vehicle-select" value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} className={inputCls}>
            <option value="">Select vehicle…</option>
            {vehicles.filter((v) => v.status !== "workshop" && v.status !== "on_test_drive").map((v) => <option key={v.id} value={v.id}>{v.reg_no} — {v.model}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="ws-ro-input" value={f.ro_number} onChange={(e) => setF({ ...f, ro_number: e.target.value })} placeholder="RO number *" className={inputCls} />
            <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={inputCls}>
              {["Periodic Service", "Repair", "Bodyshop", "Accident"].map((x) => <option key={x}>{x}</option>)}
            </select>
            <input value={f.workshop_branch} onChange={(e) => setF({ ...f, workshop_branch: e.target.value })} placeholder="Workshop branch" className={inputCls} />
            <input data-testid="ws-return-date-input" type="date" value={f.expected_return_date} onChange={(e) => setF({ ...f, expected_return_date: e.target.value })} className={inputCls} />
            <input data-testid="ws-odo-input" type="number" value={f.odo_out} onChange={(e) => setF({ ...f, odo_out: e.target.value })} placeholder="Odometer at handover" className={inputCls} />
          </div>
          <button data-testid="ws-submit-btn" onClick={handover} className="w-full rounded-xl bg-[#0E2240] py-3 text-sm font-bold text-white">Record Handover</button>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {entries.map((w) => (
          <div key={w.id} data-testid={`workshop-card-${w.ro_number}`} className={`rounded-xl border bg-white p-4 shadow-sm ${w.overdue ? "border-red-300" : "border-slate-200"}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading font-bold text-[#0E2240]">{w.reg_no} · RO {w.ro_number}</div>
                <div className="text-xs text-slate-500 mt-0.5">{w.reason} · in {d(w.handover_dt)} · expected {w.expected_return_date} · {w.days_in_workshop}d in workshop</div>
                {w.status === "closed" && <div className="text-xs mt-0.5 text-emerald-700 font-semibold">₹{(w.cost_amount || 0).toLocaleString("en-IN")} · {w.cost_category} · returned {d(w.return_dt)}</div>}
                {w.overdue && <div className="text-xs mt-0.5 font-bold text-[#BB162B]">⚠ Past expected return date</div>}
              </div>
              <StatusChip status={w.status} />
            </div>
            {w.status === "open" && canEdit && (
              returning === w.id ? (
                <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="ws-return-odo-input" type="number" value={r.odo_in} onChange={(e) => setR({ ...r, odo_in: e.target.value })} placeholder="Odometer at return" className={inputCls} />
                    <input data-testid="ws-cost-input" type="number" value={r.cost_amount} onChange={(e) => setR({ ...r, cost_amount: e.target.value })} placeholder="Cost ₹ (mandatory)" className={inputCls} />
                    <select data-testid="ws-cost-category" value={r.cost_category} onChange={(e) => setR({ ...r, cost_category: e.target.value })} className={inputCls}>
                      {["Periodic Service", "Repair", "Bodyshop", "Accident", "Tyres"].map((x) => <option key={x}>{x}</option>)}
                    </select>
                    <input value={r.work_summary} onChange={(e) => setR({ ...r, work_summary: e.target.value })} placeholder="Work summary" className={inputCls} />
                  </div>
                  <button data-testid="ws-return-submit-btn" onClick={doReturn} className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white">Accept Return</button>
                </div>
              ) : (
                <button data-testid={`ws-return-btn-${w.ro_number}`} onClick={() => { setReturning(w.id); setR({ odo_in: "", work_summary: "", cost_amount: "", cost_category: "Periodic Service" }); }}
                  className="mt-3 w-full rounded-xl border border-slate-300 py-2.5 text-sm font-bold text-slate-600">Record Return</button>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
